/**
 * THE LOADING SCREEN — something to look at while a chunk or a match lands.
 *
 * ── WHERE IT IS ACTUALLY MOUNTED ────────────────────────────────────────────
 * An earlier audit wondered whether this was orphaned. It is not, and the
 * import chain is short enough to state:
 *
 *   `main.ts` -> `mountLazy` (`engine/layout/lazy.ts`) -> `mountLoadingScreen`
 *       covers every lazy document route the router mounts.
 *   `engine/bootstrap.ts` -> `mountLoadingScreen` twice: `showOnlineMatch`
 *       (the wait between `match_start` and the ceremony) and `showMatch`
 *       (the training boot, while Pixi and the atlases land).
 *
 * So this IS the screen the player looks at before a match. It is worth the
 * art.
 *
 * ── THE TWO TESTIDS ARE A CONTRACT, NOT DECORATION ──────────────────────────
 * `test/lazy-route-clears.test.ts` asserts `data-testid="lazy-route-loading"`
 * while a chunk is in flight and `data-testid="lazy-route-failed"` with
 * `role="alert"` when the import rejects, both carrying `t('meta.loading')` and
 * `t('meta.chunk_failed')`. Both are reproduced here exactly. Do not rename.
 *
 * ── THE PLATE IS GONE, AND WHY ──────────────────────────────────────────────
 * This screen used to draw `.rga-load__plate`: a bordered 2:1 box with a
 * painted river across it, holding a `createMediaSlot` placeholder that MIGHT
 * receive a card portrait. Three things were wrong with it, and only the third
 * is cosmetic:
 *
 *  1. THE ART WAS CONDITIONAL. The portrait was resolved through
 *     `/assets/assets.manifest.json`, which `tools/assetgen` writes into the
 *     build and which is NOT in the source tree — there is no
 *     `frontend/public/assets/` to check out. Where the pipeline has not run
 *     (a fresh clone, every vitest run, any deploy built without it) the dev
 *     server answers that path with `index.html` and a 200, the resolver
 *     correctly reads it as "every url is empty", and the plate stays empty.
 *     A wait screen whose art is contingent on a build step is a wait screen
 *     that is sometimes a bordered empty box.
 *  2. WHAT WAS UNDER IT WAS THE PLACEHOLDER. `createMediaSlot` IS the dashed,
 *     hatched "art goes here" box from `engine/ui/placeholder.ts`. A stack of
 *     CSS overrides existed purely to hide its own chrome — the tell that it
 *     was the wrong primitive: a placeholder is a construction marker, and
 *     construction markers are the unfinished look, one cascade accident away
 *     from being visible.
 *  3. Even fully loaded it was wrong. A card portrait is 1024x1024 painterly
 *     art WITH its own background, so it can only ever be shown inside a frame
 *     — and a bordered rectangle with a stripe through it is not arena art.
 *
 * ── WHAT REPLACES IT: A CUT-OUT THAT IS ALWAYS THERE ────────────────────────
 * `engine/ui/hero-model.ts` resolves `/sprites/heroes/<cardId>.png` — the baked
 * transparent model renders, 40 of them, which are CHECKED IN under
 * `frontend/public/` and therefore present in every clone and every build. No
 * manifest, no fetch, no 200-that-is-really-HTML, and no build step in the way:
 * the id is either in `HERO_MODEL_IDS` or it is not, and
 * `test/hero-model-art.test.ts` already pins that set against the directory on
 * disk in both directions.
 *
 * They are also the RIGHT art, not merely the available art: a cut-out has no
 * background, so it can stand on the splash under its own silhouette shadow
 * with nothing drawn around it.
 *
 * So the hero is picked synchronously at mount, stands free on the splash with
 * its own silhouette shadow and a soft ground ellipse, and fades in when the
 * browser has decoded it. There is no box, no rim and no hatch at any point:
 *
 *  · before it decodes, the reserved space is an empty patch of splash art;
 *  · if the file 404s, the `<img>` is REMOVED and that is the end state — never
 *    a broken-image glyph, never a placeholder;
 *  · the height is reserved by CSS, not by the image, so the arrival shifts
 *    nothing.
 *
 * ── MOTION ──────────────────────────────────────────────────────────────────
 * A waiting state is the one screen where idle motion competes with nothing, so
 * the three pips pulse in sequence. Under reduced motion they are simply lit —
 * the progress information survives, the movement does not. The cut-out arrives
 * on a one-shot fade-and-rise (a state change, not idle motion), switched off
 * by `prefers-reduced-motion` and by the `is-reduced-motion` class the caller
 * can force; `REWARDS_CSS` kills both with one rule at the end of the sheet.
 *
 * ── LAYER (ADR-0005) ────────────────────────────────────────────────────────
 * SHELL-safe: `t()`, `el()`, its own CSS and `engine/ui/hero-model.ts`, which
 * is two frozen `Set`s of strings and imports nothing at all. It has to be,
 * because it is the thing shown BEFORE the rest loads — which is also why
 * `LOADING_HEROES` is a literal list of ids rather than a read of the card
 * catalogue: importing `@rga/sim` here would put the whole rules engine in
 * front of the first paint it exists to cover. `test/loading-screen.test.ts`
 * reads `shared/sim/src/cards/` off disk instead and fails the day one of those
 * ids stops naming a real unit.
 */
import { systemPrefersReducedMotion } from '../a11y/motion';
import { t } from '../i18n';
import { el, replaceChildren } from '../layout/dom';
import { createButton, type Button } from '../ui/button';
import { heroModelUrl } from '../ui/hero-model';

import { injectRewardsCss } from './rewards-css';

/** Which wait this is. Recorded on the root as `data-kind`; nothing else differs. */
export type LoadingKind = 'chunk' | 'match';

/** Resolve a card id to the URL of its cut-out, or `''` when it has none. */
export type HeroUrlResolver = (cardId: string) => string;

export interface LoadingScreenOptions {
  readonly kind?: LoadingKind;
  /** 0..1 for a determinate bar, or omitted/null for an indeterminate wait. */
  readonly progress?: number | null;
  /** Override the resolved `prefers-reduced-motion`; mostly for tests. */
  readonly reducedMotion?: boolean;
  /**
   * Which hero to start looking from. Any number or string; the same seed
   * always yields the same cut-out. Omitted, successive mounts walk the cast in
   * order — deterministic, and a player waiting three times does not see the
   * same creature three times.
   */
  readonly seed?: number | string;
  /** Card ids to choose from, in order. Defaults to {@link LOADING_HEROES}. */
  readonly cast?: readonly string[];
  /** Opt IN to the hero cut-out layer. Defaults to false — see P17. */
  readonly hero?: boolean;
  /** Show the arena splash art behind the hero. Defaults to true. */
  readonly splash?: boolean;
  /** Where the cut-out URL comes from. Defaults to `heroModelUrl()`. */
  readonly heroUrl?: HeroUrlResolver;
}

export interface LoadingScreenHandle {
  readonly root: HTMLElement;
  /** 0..1, or null to go back to indeterminate. Clamped, never throws. */
  setProgress(value: number | null): void;
  /**
   * Swap to the failure state. Idempotent; stops every timer. Given `onRetry`,
   * a "Try again" button sits under the message — a chunk that 404'd after a
   * deploy, or a dropped phone connection, is a dead end without one.
   */
  fail(error?: unknown, onRetry?: () => void): void;
  destroy(): void;
}

const PIPS = 3;

/**
 * The creatures that get to stand on the splash: two per faction, every one of
 * them a `type: "unit"` at epic or mythic, because those are the silhouettes
 * worth showing at 200px tall.
 *
 * Spells and structures are deliberately absent — they have no model at all,
 * so `heroModelUrl` would answer `''` for every one of them — and so are the
 * commons, whose renders are the least distinctive in the set.
 *
 * These are ids from `shared/sim/src/cards/*.json`, copied rather than imported
 * (see the header). `test/loading-screen.test.ts` reads that directory and
 * asserts every id here still exists, is still a unit, and still has a baked
 * cut-out, so a rename in the catalogue fails the suite instead of silently
 * emptying the screen.
 */
export const LOADING_HEROES: readonly string[] = Object.freeze([
  'ember-forgetitan',
  'hollow-reliquarch',
  'thorn-elderbriar',
  'tide-wrackwalker',
  'neutral-vaultbreaker',
  'ember-slagbrute',
  'hollow-gravebell',
  'thorn-heartwood',
  'tide-anchorstone',
  'neutral-arcwright',
]);

/**
 * The default seed, advanced once per mount.
 *
 * NOT `Math.random()` and not `Date.now()`: a counter is reproducible, so a bug
 * report that says "the third loading screen showed nothing" names one card
 * rather than a lottery. Tests pass `seed` explicitly and never depend on where
 * this happens to be.
 */
let nextSeed = 0;

/** 32-bit FNV-1a. Small, stable, and enough to spread ten ids evenly. */
function hashSeed(seed: number | string): number {
  if (typeof seed === 'number') {
    return Number.isFinite(seed) ? Math.abs(Math.trunc(seed)) : 0;
  }
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** A card id and the URL of its cut-out. */
export interface LoadingHero {
  readonly id: string;
  readonly url: string;
}

/**
 * Choose the cut-out to show, or null when none of the cast has one.
 *
 * Pure and SYNCHRONOUS — that is the point of moving off the asset manifest.
 * There is nothing to await, nothing to cancel and no late resolution that can
 * paint into a screen the player has already left. Starts at
 * `seed % cast.length` and walks the whole list, so a cast that grows a card
 * whose model has not been baked yet degrades to its neighbour rather than to
 * nothing.
 */
export function pickLoadingHero(
  seed: number | string,
  cast: readonly string[] = LOADING_HEROES,
  resolve: HeroUrlResolver = heroModelUrl,
): LoadingHero | null {
  if (cast.length === 0) return null;
  const start = hashSeed(seed) % cast.length;
  for (let i = 0; i < cast.length; i++) {
    const id = cast[(start + i) % cast.length] as string;
    const url = resolve(id);
    if (url !== '') return { id, url };
  }
  return null;
}

export function mountLoadingScreen(
  host: HTMLElement,
  options: LoadingScreenOptions = {},
): LoadingScreenHandle {
  const reduced = options.reducedMotion ?? systemPrefersReducedMotion();
  const releaseCss = injectRewardsCss(host.ownerDocument);

  const root = el('div', {
    class: `rga-rw rga-load${reduced ? ' is-reduced-motion' : ''}`,
    'data-rga': 'loading-screen',
    'data-kind': options.kind ?? 'chunk',
    // The arena splash sits behind the hero under a dark scrim, so the pips and
    // the bar stay legible. Off for a bare wait (hero:false, splash:false).
    ...(options.splash === false ? {} : { 'data-splash': 'true' }),
  });

  const pips = Array.from({ length: PIPS }, (_, i) =>
    el('span', { class: `rga-load__pip${reduced || i === 0 ? ' is-lit' : ''}` }),
  );

  /*
   * The hero layer is built and mounted NOW, whether or not a cut-out exists.
   * Its height comes from CSS, so an image landing later cannot move anything,
   * and an empty one is an empty patch of splash rather than a marked-up hole.
   *
   * `aria-hidden`: the `role="status"` line below already announces the wait,
   * and reading a creature name out mid-navigation is noise.
   */
  // P17 (owner, 2026-09-14): the loading screen shows NO hero cut-out. The
  // layer is only built when a caller opts in explicitly with `hero: true`,
  // which nothing in the app does — the splash, the pips and the bar carry it.
  const wantsHero = options.hero === true;
  const heroLayer = el('div', {
    class: 'rga-load__hero',
    'aria-hidden': 'true',
    'data-loaded': 'false',
  });

  let heroImg: HTMLImageElement | null = null;
  let onHeroLoad: (() => void) | null = null;
  let onHeroError: (() => void) | null = null;

  /**
   * Drop the cut-out and its two listeners.
   *
   * Called by BOTH `fail()` and `destroy()`. `fail()` replaces the root's
   * children, which detaches the hero but leaves the `<img>` — and an `<img>`
   * with a live `src` keeps its request in flight for a screen the player has
   * already left.
   */
  const releaseHero = (): void => {
    const img = heroImg;
    heroImg = null;
    if (img === null) return;
    if (onHeroLoad !== null) img.removeEventListener('load', onHeroLoad);
    if (onHeroError !== null) img.removeEventListener('error', onHeroError);
    onHeroLoad = null;
    onHeroError = null;
    img.remove();
  };

  if (wantsHero) {
    const pick = pickLoadingHero(
      options.seed ?? nextSeed++,
      options.cast ?? LOADING_HEROES,
      options.heroUrl ?? heroModelUrl,
    );
    if (pick !== null) {
      heroLayer.setAttribute('data-rga-hero', pick.id);
      const img = el('img', {
        class: 'rga-load__hero-img',
        src: pick.url,
        alt: '',
        decoding: 'async',
      }) as HTMLImageElement;
      onHeroLoad = () => {
        // The ONLY thing that makes the cut-out visible. Until it fires the
        // layer is at opacity 0 and the ground ellipse is hidden.
        heroLayer.setAttribute('data-loaded', 'true');
      };
      onHeroError = () => {
        // A file that is not in the build. Take the node out entirely rather
        // than leaving the browser to draw its broken-image glyph on the art.
        heroLayer.removeAttribute('data-rga-hero');
        releaseHero();
      };
      img.addEventListener('load', onHeroLoad);
      img.addEventListener('error', onHeroError);
      heroImg = img;
      heroLayer.append(img);
    }
  }

  /*
   * THE STATUS LINE IS NOT VISIBLE, AND IS NOT REMOVED. The owner asked for art
   * and a bar and nothing else, but `test/lazy-route-clears.test.ts` asserts
   * this node's testid, and a screen reader gets no other announcement that a
   * wait is under way. So it is clipped, not `display:none` — clipping keeps it
   * in the accessibility tree and in `querySelector`.
   *
   * `role="status"` and not `role="alert"`: a wait is polite, and a screen
   * reader that interrupts the user to say "Loading" on every navigation is
   * worse than one that mentions it when it gets a turn.
   */
  const message = el('p', {
    class: 'rga-load__msg rga-sr-only',
    role: 'status',
    'data-testid': 'lazy-route-loading',
    text: t('meta.loading'),
  });

  const fill = el('span', { class: 'rga-load__fill' });
  const bar = el('div', {
    class: 'rga-load__bar',
    role: 'progressbar',
    'aria-valuemin': '0',
    'aria-valuemax': '100',
    'data-rga': 'loading-bar',
  }, [fill]);
  bar.setAttribute('aria-valuetext', t('rewards.loading.progress'));

  // The pips are OUT of the hero now, in normal flow above the bar — they were
  // the plate's only other occupant and the plate is gone. They stay because
  // they are the coarse progress read for anyone who cannot see a 10px bar
  // move: one pip per third.
  replaceChildren(root, wantsHero && heroLayer, el('div', { class: 'rga-load__pips' }, pips), message, bar);
  host.append(root);

  let dead = false;

  const setProgress = (value: number | null): void => {
    if (dead) return;
    if (value === null || !Number.isFinite(value)) {
      bar.hidden = true;
      bar.removeAttribute('aria-valuenow');
      fill.style.width = '0%';
      return;
    }
    const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
    bar.hidden = false;
    bar.setAttribute('aria-valuenow', String(pct));
    fill.style.width = `${pct}%`;
    const lit = Math.min(PIPS, Math.floor(pct / (100 / PIPS)) + (pct >= 100 ? 0 : 1));
    pips.forEach((p, i) => p.classList.toggle('is-lit', i < lit));
  };

  setProgress(options.progress ?? null);

  let retryButton: Button | null = null;

  return {
    root,
    setProgress,
    fail(error?: unknown, onRetry?: () => void) {
      if (dead) return;
      releaseHero();
      if (error !== undefined) {
        console.error('[rewards] the loading screen was told the load failed', error);
      }
      const message = el('p', {
        class: 'rga-load__failed',
        role: 'alert',
        'data-testid': 'lazy-route-failed',
        text: t('meta.chunk_failed'),
      });
      if (onRetry === undefined) {
        replaceChildren(root, message);
        return;
      }
      if (retryButton !== null) retryButton.destroy();
      retryButton = createButton({
        label: t('common.retry'),
        icon: 'action-retry',
        variant: 'primary',
        testId: 'lazy-route-retry',
        onPress: () => {
          retryButton?.setLoading(true);
          onRetry();
        },
      });
      replaceChildren(root, message, retryButton.root);
    },
    destroy() {
      if (dead) return;
      dead = true;
      retryButton?.destroy();
      retryButton = null;
      releaseHero();
      root.remove();
      releaseCss();
    },
  };
}
